import React, { useState, useMemo, useEffect, useRef } from 'react';
import './DatePicker.css';

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function generateCalendarGrid(date: Date): (Date | null)[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const days: (Date | null)[] = [];
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    days.push(new Date(year, month - 1, prevMonthLastDay - i));
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(year, month, i));
  }
  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    days.push(new Date(year, month + 1, i));
  }
  return days;
}

export interface DatePickerProps {
  /** Текущая выбранная дата (для отображения месяца и выделенного дня) */
  value: Date;
  /** Вызывается при нажатии «Подтвердить выбор» */
  onConfirm: (date: Date) => void;
  /** Вызывается при закрытии (после подтверждения или при клике снаружи — родитель сам решает) */
  onClose?: () => void;
  /** Дополнительный класс для контейнера (например, для позиционирования) */
  className?: string;
  /** Показывать выбор времени (по умолчанию true). Для дат рождения обычно false. */
  showTime?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onConfirm,
  onClose,
  className = '',
  showTime = true,
}) => {
  const [pickerDate, setPickerDate] = useState<Date>(() => new Date(value));
  const [selectedHour, setSelectedHour] = useState<string>(() => String(value.getHours()).padStart(2, '0'));
  const [selectedMinute, setSelectedMinute] = useState<string>(() => String(value.getMinutes()).padStart(2, '0'));
  const [isYearMenuOpen, setIsYearMenuOpen] = useState(false);
  const yearBtnRef = useRef<HTMLButtonElement | null>(null);
  const yearMenuRef = useRef<HTMLDivElement | null>(null);
  const selectedYearRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const newDate = new Date(value);
    setPickerDate(newDate);
    setSelectedHour(String(newDate.getHours()).padStart(2, '0'));
    setSelectedMinute(String(newDate.getMinutes()).padStart(2, '0'));
    setIsYearMenuOpen(false);
  }, [value]);

  const calendarGrid = useMemo(() => generateCalendarGrid(pickerDate), [pickerDate]);
  const today = new Date();

  const changeMonth = (direction: 'prev' | 'next') => {
    setPickerDate((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      return next;
    });
  };

  const buildFinalDate = (date: Date): Date => {
    const finalDate = new Date(date);
    if (showTime) {
      const hour = parseInt(selectedHour || '0', 10);
      const minute = parseInt(selectedMinute || '0', 10);
      finalDate.setHours(Math.max(0, Math.min(23, hour)));
      finalDate.setMinutes(Math.max(0, Math.min(59, minute)));
    } else {
      finalDate.setHours(0, 0, 0, 0);
    }
    return finalDate;
  };

  const handleDateSelect = (date: Date) => {
    setPickerDate(new Date(date));
  };

  const handleDateDoubleClick = (date: Date) => {
    const selected = new Date(date);
    setPickerDate(selected);
    onConfirm(buildFinalDate(selected));
    onClose?.();
  };

  const handleTimeChange = () => {
    const updatedDate = new Date(pickerDate);
    const hour = parseInt(selectedHour || '0', 10);
    const minute = parseInt(selectedMinute || '0', 10);
    updatedDate.setHours(Math.max(0, Math.min(23, hour)));
    updatedDate.setMinutes(Math.max(0, Math.min(59, minute)));
    setPickerDate(updatedDate);
  };

  const handleConfirm = () => {
    onConfirm(buildFinalDate(pickerDate));
    onClose?.();
  };

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const minYear = 1900;
    const maxYear = currentYear + 5;
    const years: number[] = [];
    for (let y = maxYear; y >= minYear; y--) {
      years.push(y);
    }
    return years;
  }, []);

  useEffect(() => {
    if (!isYearMenuOpen) return;
    // Автоскролл к выбранному году
    requestAnimationFrame(() => {
      selectedYearRef.current?.scrollIntoView({ block: 'center' });
    });
  }, [isYearMenuOpen]);

  useEffect(() => {
    if (!isYearMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const yearBtn = yearBtnRef.current;
      const menu = yearMenuRef.current;
      if (menu && menu.contains(target)) return;
      if (yearBtn && yearBtn.contains(target)) return;
      setIsYearMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isYearMenuOpen]);

  return (
    <div className={`date-picker ${className}`.trim()} role="dialog" aria-modal="true" aria-label="Выбор даты">
      <div className="date-picker-top-bar" />
      <div className="date-picker-content">
        <div className="date-picker-month">
          <span className="date-picker-month-name" aria-label="Текущий месяц и год">
            <span>{MONTH_NAMES[pickerDate.getMonth()]}</span>{' '}
            <button
              ref={yearBtnRef}
              type="button"
              className="date-picker-year-btn"
              onClick={() => {
                setIsYearMenuOpen((v) => !v);
              }}
              aria-label="Выбрать год"
              aria-expanded={isYearMenuOpen}
            >
              <span className="date-picker-year-btn-text">{pickerDate.getFullYear()}</span>
              <svg
                className={`date-picker-year-caret ${isYearMenuOpen ? 'open' : ''}`}
                width="14"
                height="14"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </span>
          <div className="date-picker-nav">
            <button
              type="button"
              className="date-picker-month-nav"
              onClick={() => {
                changeMonth('prev');
              }}
              aria-label="Предыдущий месяц"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="#1C274C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="date-picker-month-nav"
              onClick={() => {
                changeMonth('next');
              }}
              aria-label="Следующий месяц"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="#1C274C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
        {isYearMenuOpen && (
          <div className="date-picker-year-menu" ref={yearMenuRef} role="listbox" aria-label="Выбор года">
            <div className="date-picker-year-list">
              {yearOptions.map((y) => {
                const selected = y === pickerDate.getFullYear();
                return (
                  <button
                    key={y}
                    type="button"
                    ref={selected ? selectedYearRef : undefined}
                    className={`date-picker-year-item ${selected ? 'selected' : ''}`}
                    onClick={() => {
                      setPickerDate((prev) => {
                        const next = new Date(prev);
                        next.setFullYear(y);
                        return next;
                      });
                      setIsYearMenuOpen(false);
                    }}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="date-picker-weekdays">
          {DAY_NAMES.map((day) => (
            <div key={day} className="date-picker-weekday">
              {day}
            </div>
          ))}
        </div>
        <div className="date-picker-days">
          {calendarGrid.map((date, idx) => {
            if (!date) return null;
            const isCurrentMonth = date.getMonth() === pickerDate.getMonth();
            const isToday =
              date.getDate() === today.getDate() &&
              date.getMonth() === today.getMonth() &&
              date.getFullYear() === today.getFullYear();
            const isSelected =
              date.getDate() === pickerDate.getDate() &&
              date.getMonth() === pickerDate.getMonth() &&
              date.getFullYear() === pickerDate.getFullYear();
            return (
              <button
                key={idx}
                type="button"
                className={`date-picker-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleDateSelect(date)}
                onDoubleClick={() => handleDateDoubleClick(date)}
                disabled={!isCurrentMonth}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
        {showTime && (
          <div className="date-picker-time-picker">
            <label className="date-picker-time-label">Введите время</label>
            <div className="date-picker-time-inputs">
              <input
                type="number"
                min="0"
                max="23"
                value={selectedHour}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 23)) {
                    setSelectedHour(val);
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  const hour = val === '' ? 0 : Math.max(0, Math.min(23, parseInt(val, 10) || 0));
                  setSelectedHour(String(hour).padStart(2, '0'));
                  handleTimeChange();
                }}
                className="date-picker-time-input"
                placeholder="00"
              />
              <span className="date-picker-time-separator">:</span>
              <input
                type="number"
                min="0"
                max="59"
                value={selectedMinute}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 59)) {
                    setSelectedMinute(val);
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  const minute = val === '' ? 0 : Math.max(0, Math.min(59, parseInt(val, 10) || 0));
                  setSelectedMinute(String(minute).padStart(2, '0'));
                  handleTimeChange();
                }}
                className="date-picker-time-input"
                placeholder="00"
              />
            </div>
          </div>
        )}
        <button type="button" className="date-picker-confirm" onClick={handleConfirm}>
          Подтвердить выбор
        </button>
      </div>
      <div className="date-picker-bottom-bar" />
    </div>
  );
};

export default DatePicker;
